import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";

// --- Data & Types ---

const ROLE_CATEGORIES = {
    "Creative": ["Writer", "Director", "Showrunner", "Creative Producer"],
    "Production": ["Executive Producer", "Producer", "Line Producer", "Production Manager", "Post-Production Supervisor"],
    "Business & Distribution": ["Sales Agent", "Distribution Partner", "Business Affairs", "Talent Manager/Agent"],
    "Growth & Marketing": ["Social Media Manager", "Marketing/PR", "Audience Growth Strategist"],
    "Talent": ["On-Screen Talent / Host", "Composer / Musician"],
    "Other": ["Other"]
};

// --- Components ---

export default function CollaboratorOnboardingPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { checkAuth } = useAuthStore();
    const queryClient = useQueryClient();

    const [step, setStep] = useState<"loading" | "roles" | "context">("loading");
    const [invitationData, setInvitationData] = useState<any>(null);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [otherRole, setOtherRole] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Load Invitation Data & Check Auth
    useEffect(() => {
        const init = async () => {
            if (!token) return;

            // 1. Load Invitation Data (validate token + get context)
            try {
                const data = await apiClient.getInvitationDetails(token);

                setInvitationData(data);
                if (data.roles && data.roles.length > 0) {
                    setSelectedRoles(data.roles);
                }

                // 🔍 Debug: backend invitation payload + current URL
                console.log("🔍 INVITE DEBUG", {
                    invitation: data,
                    token,
                    location: window.location.href,
                });

                // 2. Ensure we have up-to-date auth
                const user = await checkAuth();

                // 🔍 Debug: current authenticated user
                console.log("🔍 USER DEBUG", {
                    user,
                    isLoggedIn: !!user,
                });

                // 3. If no user, send to login/signup as the invited email, with redirect back here
                if (!user) {
                    const onboardingPath = `/invite/onboarding/${token}`;
                    if (data.is_existing_user) {
                        navigate(
                            `/login?email=${encodeURIComponent(data.email)}&redirectTo=${encodeURIComponent(onboardingPath)}`
                        );
                    } else {
                        navigate(
                            `/signup?email=${encodeURIComponent(data.email)}&redirectTo=${encodeURIComponent(onboardingPath)}`
                        );
                    }
                    return;
                }

                // 4. If logged-in user email does not match invitation email, force re-auth as invited user
                if (
                    data.email &&
                    user.email &&
                    user.email.toLowerCase() !== data.email.toLowerCase()
                ) {
                    const onboardingPath = `/invite/onboarding/${token}`;
                    const authPath = data.is_existing_user ? "/login" : "/signup";
                    navigate(
                        `${authPath}?email=${encodeURIComponent(data.email)}&redirectTo=${encodeURIComponent(onboardingPath)}`
                    );
                    return;
                }

                // 5. At this point we have the correct user logged in.
                // If invitation was already accepted, skip roles and deep-link to the unified project V2 flow.
                if (data.already_accepted) {
                    toast.success("Welcome back! You've already joined this project.");
                    try {
                        await apiClient.updateCurrentUser({ onboarding_completed: true });
                        localStorage.setItem('collaborator_onboarding_completed', Date.now().toString());
                    } catch (error) {
                        console.error("Failed to mark onboarding as complete for collaborator:", error);
                    }

                    if (data.project_id) {
                        localStorage.setItem(`welcome-flow-seen-${data.project_id}`, 'true');
                        // Use ProjectV2Router so collaborators and owners share the same entry flow
                        navigate(`/project-v2/${data.project_id}`);
                    } else {
                        navigate("/dashboard");
                    }
                    return;
                }

                // 6. Correct user + pending invitation → show Roles step
                setStep("roles");

            } catch (error) {
                console.error("Failed to load invitation:", error);
                toast.error("Invalid or expired invitation link.");
                navigate("/");
            }
        };
        init();
    }, [token, navigate, checkAuth]);

    const handleRoleToggle = (role: string) => {
        setSelectedRoles(prev =>
            prev.includes(role)
                ? prev.filter(r => r !== role)
                : [...prev, role]
        );
    };

    const handleRolesSubmit = () => {
        // If "Other" is selected and text is entered, add it to roles list effectively? 
        // The requirement says "Other (free text)". 
        // We'll append the custom text to the roles list if "Other" is checked.
        let finalRoles = [...selectedRoles];
        if (finalRoles.includes("Other") && otherRole.trim()) {
            finalRoles = finalRoles.filter(r => r !== "Other"); // Remove "Other" placeholder
            finalRoles.push(`Other: ${otherRole.trim()}`);
        }

        // Pass roles to next step or save state? 
        // We'll store normalized roles in state for the final commit
        setSelectedRoles(finalRoles);
        setStep("context");
    };

    const handleFinalAccept = async () => {
        if (!token || !invitationData) return;

        setIsSubmitting(true);
        try {
            // Accept invitation with selected roles
            await apiClient.acceptInvitation(token, selectedRoles);

            toast.success("Welcome to the team!");

            // Mark onboarding as completed for collaborators to prevent them from being redirected to onboarding flow
            try {
                await apiClient.updateCurrentUser({ onboarding_completed: true });
                await checkAuth(); // Refresh user state
                
                // Set a timestamp to track recent collaborator onboarding completion
                localStorage.setItem('collaborator_onboarding_completed', Date.now().toString());
            } catch (error) {
                console.error("Failed to mark onboarding as complete for collaborator:", error);
                // Don't block the flow if this fails
            }

            // Set welcome flow seen to skip the score/task pages for collaborators
            if (invitationData?.project_id) {
                localStorage.setItem(`welcome-flow-seen-${invitationData.project_id}`, 'true');
            }

            // Invalidate project and score queries to ensure fresh data when redirecting
            if (invitationData?.project_id) {
                await queryClient.invalidateQueries({ queryKey: ['project', invitationData.project_id] });
                await queryClient.invalidateQueries({ queryKey: ['artieScoreStatus', invitationData.project_id] });
            }

            // After accepting, always enter through ProjectV2Router so the unified
            // project flow (including any collaborator role logic) runs exactly once.
            navigate(`/project-v2/${invitationData.project_id}`);
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || "Failed to accept invitation");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (step === "loading") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFDFD] flex flex-col items-center justify-center p-4 font-sans text-[#111]">
            <div className="w-full max-w-[37.5rem]">

                {/* STEP 1: ROLES */}
                {step === "roles" && (
                    <Card className="border-none shadow-lg shadow-gray-100/50">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-xl font-bold">What's your role?</CardTitle>
                            <CardDescription>
                                Select all that apply for this project.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="max-h-[60vh] overflow-y-auto pr-2">
                            <div className="space-y-6">
                                {Object.entries(ROLE_CATEGORIES).map(([category, roles]) => (
                                    <div key={category}>
                                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{category}</h3>
                                        <div className="space-y-2">
                                            {roles.map((role) => (
                                                <div key={role} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer" onClick={(e) => {
                                                    // Prevent double triggering if they clicked the checkbox or label directly
                                                    if ((e.target as HTMLElement).tagName !== 'LABEL' && !(e.target as HTMLElement).closest('button')) {
                                                        handleRoleToggle(role);
                                                    }
                                                }}>
                                                    <Checkbox
                                                        id={role}
                                                        checked={selectedRoles.includes(role)}
                                                        onCheckedChange={() => handleRoleToggle(role)}
                                                    />
                                                    <Label htmlFor={role} className="cursor-pointer font-normal text-base flex-1">{role}</Label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {selectedRoles.includes("Other") && (
                                    <div className="pl-8 pt-0 animate-in fade-in slide-in-from-top-2">
                                        <Input
                                            placeholder="Please specify..."
                                            value={otherRole}
                                            onChange={(e) => setOtherRole(e.target.value)}
                                            className="bg-gray-50/50"
                                        />
                                    </div>
                                )}
                            </div>
                        </CardContent>
                        <CardFooter className="pt-4 border-t">
                            <Button onClick={handleRolesSubmit} className="w-full h-11 text-base">
                                Continue
                            </Button>
                        </CardFooter>
                    </Card>
                )}

                {/* STEP 2: CONTEXT */}
                {step === "context" && (
                    <Card className="border-none shadow-lg shadow-gray-100/50 text-center">
                        <CardHeader className="pb-2">
                            <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                                {invitationData.project_name?.charAt(0) || "P"}
                            </div>
                            <CardTitle className="text-2xl font-bold">Welcome aboard!</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6 pt-4">
                            <div className="space-y-2">
                                <p className="text-lg text-gray-600">
                                    You've been invited to help <span className="font-semibold text-gray-900">{invitationData.owner_name}</span> with <span className="font-semibold text-gray-900">{invitationData.project_name}</span>.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 text-left">
                                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Your Task</div>
                                <h3 className="text-lg font-bold text-gray-900 mb-1">{invitationData.task_name || "Assigned Task"}</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {invitationData.task_description || "Help complete the requirements for this project."}
                                </p>
                            </div>
                        </CardContent>
                        <CardFooter>
                            <Button onClick={handleFinalAccept} disabled={isSubmitting} className="w-full h-12 text-base font-semibold shadow-md hover:shadow-lg transition-all">
                                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Get Started"}
                            </Button>
                        </CardFooter>
                    </Card>
                )}

            </div>
        </div>
    );
}